import React, { useState } from 'react';
import { Card, Button, Row, Col, Badge, Alert, Spinner } from 'react-bootstrap';
import { ExternalLink, Trash2, AlertTriangle } from 'lucide-react';
import { IdBadge, IdBadgeType } from '../../../../../components/ui/badge/IdBadge';
import { ROUTES } from '../../../../../routes/routes';
import { useNavigate } from 'react-router-dom';
import { ScrapingStatus } from '../../../../../models/Coupang';
import type { CpGpMatchedProduct } from '../../../../../models/Coupang';
import type { GlowpickProduct } from '../../../../../models/Glowpick';
import { glowpickClient } from '../../../../../infrastructure/GlowpickClient';
import { getConfig } from '../../../../../config';

interface MatchingInfoSectionProps {
  matchedProduct: CpGpMatchedProduct;
  glowpickProduct?: GlowpickProduct | undefined;
  cmWorkflowData?: any;
  cpProductId?: string | undefined;
  cpItemId?: string | undefined;
  refetchMatchedProduct?: () => Promise<any>;
}

export const MatchingInfoSection: React.FC<MatchingInfoSectionProps> = ({
  matchedProduct,
  glowpickProduct,
  cmWorkflowData,
  cpProductId,
  cpItemId,
  refetchMatchedProduct,
}) => {
  const navigate = useNavigate();
  const [deletingSkus, setDeletingSkus] = useState<Set<string>>(new Set());

  const handleDeleteSkuFromGlowpick = async (skuToDelete: string) => {
    if (!matchedProduct?.gpProductId) {
      alert('No Glowpick Product ID available');
      return;
    }

    if (
      !confirm(
        `Delete SKU "${skuToDelete}"?\n\nThis will:\n1. Remove the SKU from Glowpick metadata\n2. Delete the WordPress product\n\n⚠️ This action cannot be undone!`,
      )
    ) {
      return;
    }

    setDeletingSkus((prev) => new Set(prev).add(skuToDelete));

    try {
      // Step 1: Delete WordPress product
      const config = getConfig();
      const deleteResponse = await fetch(`${config.apiUrl}/woocommerce/products/${skuToDelete}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!deleteResponse.ok) {
        throw new Error(`Failed to delete WordPress product: ${deleteResponse.statusText}`);
      }

      console.info(`✅ WordPress product ${skuToDelete} deleted successfully`);

      // Step 2: Remove SKU from Glowpick metadata
      const currentSsProducts = glowpickProduct?.metadata?.ssProducts || [];
      const updatedSsProducts = currentSsProducts.filter((sku: string) => sku !== skuToDelete);

      await glowpickClient.updateProductMetadata(matchedProduct.gpProductId, {
        ssProducts: updatedSsProducts,
      });

      console.info(`✅ SKU ${skuToDelete} removed from Glowpick metadata`);

      alert(`SKU "${skuToDelete}" deleted successfully from both WordPress and Glowpick!`);

      if (refetchMatchedProduct) {
        await refetchMatchedProduct();
      }
    } catch (error) {
      console.error('Failed to delete SKU:', error);
      alert(`Failed to delete SKU: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setDeletingSkus((prev) => {
        const newSet = new Set(prev);
        newSet.delete(skuToDelete);
        return newSet;
      });
    }
  };

  const renderIdBadges = () => {
    return (
      <div className="d-flex align-items-center gap-2 flex-wrap">
        <IdBadge type={IdBadgeType.GPI} value={matchedProduct.gpProductId} size="sm" />
        <IdBadge type={IdBadgeType.CPPI} value={matchedProduct.cpProductId} size="sm" />
        <IdBadge type={IdBadgeType.CPII} value={matchedProduct.cpItemId} size="sm" />
      </div>
    );
  };

  const renderScrapingStatus = () => {
    const cpUniqueId = `${cpProductId}-${cpItemId}`;
    const cpProducts = glowpickProduct?.metadata?.cpProducts;
    const matchingProduct = cpProducts?.find((product) => product.cpUniqueId === cpUniqueId);

    return (
      <div>
        <small className="text-muted fw-bold">Scraping Status</small>
        <div className="mt-1">
          {matchedProduct.scrapingStatus && (
            <Badge
              bg={
                matchedProduct.scrapingStatus === ScrapingStatus.COMPLETED
                  ? 'success'
                  : matchedProduct.scrapingStatus === ScrapingStatus.FAILED
                    ? 'danger'
                    : matchedProduct.scrapingStatus === ScrapingStatus.IN_PROGRESS
                      ? 'warning'
                      : 'secondary'
              }
              style={{ fontSize: '0.75rem' }}
            >
              {matchedProduct.scrapingStatus}
            </Badge>
          )}
          {matchingProduct?.operator && (
            <span className="ms-3">
              <small className="text-muted">
                by {matchingProduct.operator} ({new Date(matchingProduct.operatedAt).toLocaleDateString()})
              </small>
            </span>
          )}
        </div>
      </div>
    );
  };

  const getSkuList = () => {
    const ssProducts = glowpickProduct?.metadata?.ssProducts;
    const cmWorkflow = cmWorkflowData?.[0];
    const skuMappings = cmWorkflow?.skuCpiiMappings;
    const cmSkus = cmWorkflow?.skus;

    let skuList: string[] = [];
    if (Array.isArray(skuMappings) && skuMappings.length > 0) {
      skuList = skuMappings
        .filter((mapping: any) => mapping && typeof mapping === 'object' && mapping.sku)
        .map((mapping: any) => mapping.sku)
        .filter((sku: any) => typeof sku === 'string' && sku.trim());
    } else if (Array.isArray(cmSkus) && cmSkus.length > 0) {
      skuList = cmSkus.filter((sku: any) => typeof sku === 'string');
    } else if (Array.isArray(ssProducts) && ssProducts.length > 0) {
      skuList = ssProducts.filter((sku: any) => typeof sku === 'string');
    }

    // Remove duplicates
    return [...new Set(skuList)];
  };

  const renderSkuMappingStatus = () => {
    const glowpickSkus = glowpickProduct?.metadata?.ssProducts || [];
    const cmWorkflow = cmWorkflowData?.[0];
    const skuMappings = cmWorkflow?.skuCpiiMappings;
    const currentWave = cmWorkflow?.wave;

    let cmWorkflowSkus: string[] = [];
    if (Array.isArray(skuMappings) && skuMappings.length > 0) {
      cmWorkflowSkus = skuMappings
        .filter((mapping: any) => mapping && typeof mapping === 'object' && mapping.sku)
        .map((mapping: any) => mapping.sku)
        .filter((sku: any) => typeof sku === 'string' && sku.trim());
    }

    const skusNotInCmWorkflow = glowpickSkus.filter((sku: string) => !cmWorkflowSkus.includes(sku));
    const syncedSkus = glowpickSkus.filter((sku: string) => cmWorkflowSkus.includes(sku));

    // Wave 6+: SKUs in Glowpick but not in CM Workflow are considered orphaned (can be deleted)
    // Wave < 6: SKUs in Glowpick but not in CM Workflow are legacy SKUs (valid, created before CM Workflow system)
    const waveNumber = currentWave ? parseInt(currentWave, 10) : 0;
    const isWave6OrAbove = waveNumber >= 6;
    const orphanedSkus = isWave6OrAbove ? skusNotInCmWorkflow : [];
    const legacySkus = !isWave6OrAbove ? skusNotInCmWorkflow : [];

    if (glowpickSkus.length === 0 && cmWorkflowSkus.length === 0) {
      return null;
    }

    return (
      <div className="border-top pt-3">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <div className="d-flex align-items-center gap-2">
            <small className="text-muted fw-bold">SKU Mapping Status</small>
            {currentWave && (
              <Badge bg="secondary" style={{ fontSize: '0.65rem' }}>
                Wave {currentWave}
              </Badge>
            )}
          </div>
        </div>
        <div className="mt-2 p-3 bg-light rounded">
          <Row className="g-3">
            <Col md={6}>
              <div className="mb-2">
                <div className="d-flex align-items-center gap-2">
                  <strong style={{ fontSize: '0.8rem' }}>Synced SKUs</strong>
                  <Badge bg="success" pill style={{ fontSize: '0.65rem' }}>
                    {syncedSkus.length}
                  </Badge>
                </div>
                <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                  In both Glowpick & CM Workflow
                </div>
              </div>
              <div className="d-flex flex-wrap gap-1">
                {syncedSkus.length > 0 ? (
                  syncedSkus.map((sku: string, index: number) => (
                    <IdBadge key={`synced-${index}`} type={IdBadgeType.SKU} value={sku} size="sm" />
                  ))
                ) : (
                  <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                    No synced SKUs
                  </span>
                )}
              </div>
            </Col>

            {!isWave6OrAbove && (
              <Col md={6}>
                <div className="mb-2">
                  <div className="d-flex align-items-center gap-2">
                    <strong style={{ fontSize: '0.8rem' }}>Legacy SKUs</strong>
                    <Badge bg="secondary" pill style={{ fontSize: '0.65rem' }}>
                      {legacySkus.length}
                    </Badge>
                  </div>
                  <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                    In Glowpick but not in CM Workflow (Wave {'<'} 6)
                  </div>
                </div>
                <div className="d-flex flex-wrap gap-1">
                  {legacySkus.length > 0 ? (
                    legacySkus.map((sku: string, index: number) => (
                      <div key={`legacy-${index}`} className="p-1 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                        <IdBadge type={IdBadgeType.SKU} value={sku} size="sm" />
                      </div>
                    ))
                  ) : (
                    <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                      No legacy SKUs
                    </span>
                  )}
                </div>
                {legacySkus.length > 0 && (
                  <div className="mt-2">
                    <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                      ℹ️ Valid SKUs created before CM Workflow system
                    </small>
                  </div>
                )}
              </Col>
            )}

            {isWave6OrAbove && (
              <Col md={6}>
                <div className="mb-2">
                  <div className="d-flex align-items-center gap-2">
                    <strong style={{ fontSize: '0.8rem' }}>Problematic SKUs</strong>
                    <Badge bg={orphanedSkus.length > 0 ? 'danger' : 'success'} pill style={{ fontSize: '0.65rem' }}>
                      {orphanedSkus.length}
                    </Badge>
                  </div>
                  <div
                    className={orphanedSkus.length > 0 ? 'text-danger' : 'text-muted'}
                    style={{ fontSize: '0.7rem', fontWeight: orphanedSkus.length > 0 ? 500 : 400 }}
                  >
                    {orphanedSkus.length > 0
                      ? `In Glowpick but not in CM Workflow (Wave ${currentWave})`
                      : `No problematic SKUs (Wave ${currentWave})`}
                  </div>
                </div>
                {orphanedSkus.length > 0 ? (
                  <>
                    <Alert variant="danger" className="py-2 mb-2" style={{ fontSize: '0.75rem', fontWeight: 500 }}>
                      <AlertTriangle size={14} className="me-1" />
                      <strong>Deletion Recommended:</strong> Wave 6+ SKUs must be managed in CM Workflow
                    </Alert>
                    <div className="d-flex flex-column gap-2">
                      {orphanedSkus.map((sku: string, index: number) => (
                        <div
                          key={`orphaned-${index}`}
                          className="d-flex align-items-center justify-content-between p-2 border rounded bg-white"
                        >
                          <IdBadge type={IdBadgeType.SKU} value={sku} size="sm" />
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeleteSkuFromGlowpick(sku)}
                            disabled={deletingSkus.has(sku)}
                            style={{ fontSize: '0.65rem', padding: '2px 8px' }}
                          >
                            {deletingSkus.has(sku) ? (
                              <>
                                <Spinner
                                  animation="border"
                                  size="sm"
                                  className="me-1"
                                  style={{ width: '10px', height: '10px' }}
                                />
                                Deleting...
                              </>
                            ) : (
                              <>
                                <Trash2 size={10} className="me-1" />
                                Delete
                              </>
                            )}
                          </Button>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="text-success d-flex align-items-center gap-1" style={{ fontSize: '0.75rem' }}>
                    <span>✅</span>
                    <span>All SKUs in Glowpick are also in CM Workflow</span>
                  </div>
                )}
              </Col>
            )}
          </Row>
        </div>
      </div>
    );
  };

  const renderLinks = () => {
    const skuList = getSkuList();

    return (
      <div>
        <small className="text-muted fw-bold">Links</small>
        <div className="mt-2 d-flex gap-2 flex-wrap">
          <Button
            as="a"
            href={`https://www.glowpick.com/products/${matchedProduct.gpProductId}`}
            target="_blank"
            rel="noopener noreferrer"
            variant="outline-danger"
            size="sm"
            style={{ fontSize: '0.75rem' }}
          >
            <ExternalLink size={12} className="me-1" />
            Glowpick
          </Button>
          <Button
            as="a"
            href={`https://www.coupang.com/vp/products/${matchedProduct.cpProductId}?itemId=${matchedProduct.cpItemId}`}
            target="_blank"
            rel="noopener noreferrer"
            variant="outline-primary"
            size="sm"
            style={{ fontSize: '0.75rem' }}
          >
            <ExternalLink size={12} className="me-1" />
            Coupang
          </Button>
          {skuList.length > 0 &&
            skuList.map((sku: string, index: number) => (
              <Button
                key={`links-${index}`}
                as="a"
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  navigate(ROUTES.catalog.ssEditorWithSku(String(sku)));
                }}
                variant="outline-success"
                size="sm"
                style={{ fontSize: '0.75rem' }}
              >
                <ExternalLink size={12} className="me-1" />
                SS Editor ({sku})
              </Button>
            ))}
        </div>
      </div>
    );
  };

  return (
    <Card className="mb-4">
      <Card.Header>
        <h6 className="mb-0">Matching Information</h6>
      </Card.Header>
      <Card.Body>
        <Row className="g-3 mb-3">
          <Col md={12}>{renderIdBadges()}</Col>
        </Row>

        <Row className="g-3 mb-3">
          <Col md={6}>{renderScrapingStatus()}</Col>
          <Col md={6}>
            <div>
              <small className="text-muted fw-bold">Last Updated</small>
              <div style={{ fontSize: '0.8rem' }}>
                {matchedProduct.updatedAt ? new Date(matchedProduct.updatedAt).toLocaleString() : 'N/A'}
              </div>
            </div>
          </Col>
        </Row>

        <Row className="g-3 mb-3">
          <Col md={12}>{renderSkuMappingStatus()}</Col>
        </Row>

        <Row className="g-3">
          <Col md={12}>{renderLinks()}</Col>
        </Row>
      </Card.Body>
    </Card>
  );
};
